<template>
  <div>
    <ContestEntryItem class="contest-entry-item" :name="$t('contestEntryDataForm.selectedEvent')">
      <div class="contest-data">
        <img :src="contestImage" alt="contest image" class="contest-image" loading="lazy" />
        <div class="contest-name">{{ contest.name }}</div>
      </div>
    </ContestEntryItem>

    <!-- Enable Enter Contest -->
    <template v-if="canEnterContest">
      <template v-if="contest.categories">
        <ContestEntryItem
          class="contest-entry-item"
          :name="$t('contestEntryDataForm.category')"
          :required="isConfirm ? undefined : true"
          :error="errorMessage('category')"
        >
          <SelectBoxV2
            v-model="internalValue.category"
            :options="categoryOptions"
            :placeholder="$t('contestEntryDataForm.categoryPlaceholder')"
            :readonly="isConfirm"
            :error="!!errorMessage('category')"
          />
        </ContestEntryItem>
      </template>

      <ContestEntryItem
        v-if="!contest.categories || internalValue.category"
        class="contest-entry-item"
        :name="$t('contestEntryDataForm.submissionWork')"
        :description="
          isConfirm
            ? undefined
            : contest.platform === 'nola'
            ? $t('contestEntryDataForm.submissionWorkDescriptionNola')
            : $t('contestEntryDataForm.submissionWorkDescriptionNolaNovel')
        "
        :required="isConfirm ? undefined : true"
        :error="errorMessage('novel')"
      >
        <SelectBoxV2
          v-if="contest.platform === 'nola'"
          v-model="internalValue.novel"
          :options="novelOptions"
          :placeholder="$t('contestEntryDataForm.novelPlaceholder')"
          placeholderImage="/img/placeholders/novel.png"
          :readonly="isConfirm"
          :error="!!errorMessage('category')"
        />
        <!-- Nolaノベルの場合は専用のUIを出す -->
        <template v-else>
          <div class="contest-publish-condition">
            <button @click="openNolaNovelHelpDialog()" class="help-icon">？</button>
            <div>{{ $t("contestEntryDataForm.submissionConditionLabel") }}</div>
            <div v-for="(condition, index) in contest.publishCondition" :key="index" class="publish-condition">
              {{
                condition === "public"
                  ? $t("contestEntryDataForm.publicStatus.public")
                  : condition === "limitedPublic"
                  ? $t("contestEntryDataForm.publicStatus.limitedPublic")
                  : ""
              }}
            </div>
          </div>
          <ContestNolaNovelSelectItem
            v-model="internalValue.novel"
            :options="novelOptions"
            :publishCondition="contest.publishCondition"
            :placeholder="$t('contestEntryDataForm.novelPlaceholder')"
            placeholderImage="/img/placeholders/novel.png"
            :readonly="isConfirm"
            :error="!!errorMessage('category')"
          >
          </ContestNolaNovelSelectItem>

          <div
            v-if="nolaNovelAssociated"
            class="link"
            @click="openNolaNovelPage(`/novel/${nolaNovelAssociated?.id}`)"
            target="_blank"
          >
            {{ $t("contestEntryDataForm.confirmSubmission") }}{{ nolaNovelAssociated?.title }}
          </div>
        </template>
      </ContestEntryItem>

      <template v-if="internalValue.novel">
        <template v-for="entryItem in contestEntryItems">
          <ContestEntryItem
            class="contest-entry-item"
            :name="entryItem.name"
            :description="isConfirm ? undefined : entryItem.description"
            :required="isConfirm ? undefined : !entryItem.optional"
            :error="errorMessage(`entryItems.${entryItem.id}`)"
            :key="`${entryItem.id}_${internalValue.novel.id}`"
          >
            <template
              v-if="
                entryItem.type === 'string' ||
                entryItem.type === 'number' ||
                entryItem.type === 'title' ||
                entryItem.type === 'synopsis'
              "
            >
              <InputTextV2
                :initialValue="inputInitialValue(entryItem.type)"
                :type="entryItem.type === 'number' ? 'number' : 'text'"
                :placeholder="$t('contestEntryDataForm.inputPlaceholder', { name: entryItem.name })"
                :min="entryItem.type === 'number' ? entryItem.min : undefined"
                :max="entryItem.type === 'number' ? entryItem.max : undefined"
                :minlength="entryItem.type !== 'number' ? entryItem.min : undefined"
                :maxlength="entryItem.type !== 'number' ? entryItem.max : undefined"
                :readonly="entryItem.type === 'title' || isConfirm"
                :rows="entryItem.multiLine ? 5 : undefined"
                :isDisplayCount="entryItem.max !== undefined && !isConfirm"
                :error="!!errorMessage(`entryItems.${entryItem.id}`)"
                v-model="internalValue.entryItems[entryItem.id]"
              />
            </template>
            <template v-if="entryItem.type === 'select' || entryItem.type === 'plot'">
              <SelectBoxV2
                v-model="internalValue.entryItems[entryItem.id]"
                :options="entryItem.type === 'plot' ? plotOptions : simpleOptions(entryItem.options)"
                :placeholder="$t('contestEntryDataForm.selectPlaceholder', { name: entryItem.name })"
                :readonly="isConfirm"
                :error="!!errorMessage(`entryItems.${entryItem.id}`)"
              />
            </template>
            <template v-if="entryItem.type === 'characters'">
              <SelectBoxV2
                v-if="!isConfirm"
                class="margin-bottom-10"
                :onClickHandler="() => selectCharacterDialog(entryItem.id)"
                :text="$t('contestEntryDataForm.submitDataText', { count: selectedCharactersCount(entryItem.id) })"
                :error="!!errorMessage(`entryItems.${entryItem.id}`)"
              />
              <template v-if="internalValue.entryItems[entryItem.id] && internalValue.entryItems[entryItem.id].length">
                <div class="list-title">{{ $t("contestEntryDataForm.selectedCharactersTitle") }}</div>
                <div class="list">
                  <template v-for="character in internalValue.entryItems[entryItem.id]">
                    <CharacterItem
                      class="item"
                      :novelId="internalValue.novel.id"
                      :character="character"
                      :key="character.characterId"
                    />
                  </template>
                </div>
              </template>
            </template>
            <template v-if="entryItem.type === 'manuscripts'">
              <SelectBoxV2
                v-if="!isConfirm"
                class="margin-bottom-10"
                :onClickHandler="() => selectManuscriptsDialog(entryItem.id)"
                :text="$t('contestEntryDataForm.submitDataText', { count: selectedManuscriptsCount(entryItem.id) })"
                :error="!!errorMessage(`entryItems.${entryItem.id}`)"
              />
              <template v-if="internalValue.entryItems[entryItem.id] && internalValue.entryItems[entryItem.id].length">
                <div class="list-title">{{ $t("contestEntryDataForm.selectedManuscriptsTitle") }}</div>
                <div class="list">
                  <template v-for="manuscript in internalValue.entryItems[entryItem.id]">
                    <div class="item" :key="manuscript.key">
                      {{ manuscript.title || $t("contestEntryDataForm.untitledManuscript") }}
                    </div>
                  </template>
                </div>
                <div v-if="!isConfirm" class="count">
                  <span>{{
                    $t("contestEntryDataForm.totalCharacterCount", {
                      count: selectedManuscriptsTextCount(entryItem.id),
                    })
                  }}</span>
                </div>
              </template>
            </template>
            <template v-if="entryItem.type === 'nolaNovelCheckboxGroups'">
              <NolaNovelCheckboxGroups
                v-model="internalValue.entryItems[entryItem.id]"
                :nolaNovelItem="nolaNovelAssociated"
                :settings="entryItem.checkboxGroupsSettings"
                :readonly="isConfirm"
                :error="!!errorMessage('nolaNovelCheckboxGroups')"
              />
            </template>
          </ContestEntryItem>
        </template>
      </template>
    </template>

    <!-- Disable Enter Contest -->
    <template v-else>
      <div class="contest-status">{{ contestStatus }}</div>
    </template>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { ContestEntryItemModel, NolaContest } from "@/lib/models/nolaContest";
import ContestEntryItem from "@/components/molecules/ContestEntryItem.vue";
import InputTextV2 from "@/components/atoms/InputTextV2.vue";
import SelectBoxV2, { Option } from "@/components/atoms/SelectBoxV2.vue";
import CharacterItem from "@/components/atoms/CharacterItem.vue";
import { Dialog } from "@/lib/utils";
import ContestEntryDataDialog, { ContestEntryDataDialogProps } from "@/components/ui/ContestEntryDataDialog.vue";
import { Character, isSubPlot, Manuscript, Novel, Plot, SubPlot } from "@/lib/models";
import { ValidationError } from "@/pages/EventContestEntry.vue";
import { compareAsc } from "date-fns";
import ContestNolaNovelSelectItem from "@/components/molecules/ContestNolaNovelSelectItem.vue";
import NolaNovelCheckboxGroups from "@/components/molecules/NolaNovelCheckboxGroups.vue";
import SimpleDialog, { SimpleDialogProps } from "@/components/ui/dialogs/SimpleDialog.vue";
import { UserClient } from "@/lib/clients";

export default Vue.extend<unknown, Methods, Computed, Props>({
  components: {
    ContestEntryItem,
    InputTextV2,
    SelectBoxV2,
    CharacterItem,
    ContestNolaNovelSelectItem,
    NolaNovelCheckboxGroups,
  },
  props: {
    value: Object,
    contest: Object as PropType<NolaContest>,
    contestImage: String as PropType<string>,
    isConfirm: Boolean as PropType<boolean>,
    errors: {
      type: Array as PropType<ValidationError[]>,
      default: () => [],
      required: false,
    },
  },

  async created() {
    if (this.contest.platform === "nolaNovel") {
      // NolaノベルのコンテストでNolaノベルに未登録の場合はイベントページに遷移
      const userClient = new UserClient();
      const results = await userClient.checkConnectedOtherServices();
      if (!results.nolaNovel.result) {
        this.$router.push(`/event/contest/${this.contest.id}`);
      }
    }
  },
  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    contestEntryItems() {
      const { entryItems } = this.contest;
      const entryItemsWithCategory =
        (this.internalValue.category &&
          this.contest.categories!.find((category) => category.id === this.internalValue.category.id)!.entryItems) ||
        [];
      const mergedItems = [...entryItems, ...entryItemsWithCategory] as ContestEntryItemModel[];
      const sortedItems = mergedItems.sort((a, b) => (a.order || 0) - (b.order || 0));
      return sortedItems;
    },
    novels() {
      return this.$store.getters["novelModule/novels"];
    },
    novel() {
      const { id } = this.internalValue.novel;
      return this.novels.find((novel) => novel.novelId === id);
    },
    plots() {
      const defaultPlot = this.$store.getters["plotModule/plots"] as Plot | null;
      if (!defaultPlot) return [];
      const subPlots = this.$store.getters["plotModule/subPlots"] as SubPlot[];
      return [defaultPlot, ...subPlots];
    },
    characters() {
      return this.$store.getters["characterModule/characterList"];
    },
    manuscripts() {
      return this.$store.getters["manuscriptModule/manuscriptList"];
    },
    novelTitle() {
      const { novel } = this;
      return (novel as Novel).title ?? "";
    },
    novelSummary() {
      const { novel } = this;
      return (novel as Novel).description ?? "";
    },
    categoryOptions() {
      const { categories } = this.contest;
      return categories!.map((category) => ({
        id: category.id,
        name: category.name,
      }));
    },
    novelOptions() {
      const { novels } = this;
      return novels.map((novel) => ({
        id: novel.novelId,
        name: novel.title,
        image: this.novelImageKey(novel),
        associatedData: novel.associatedData,
        updatedAtLatestDataInNovel: new Date(novel.updatedAtLatestDataInNovel).getTime(),
      }));
    },
    plotOptions() {
      const { plots } = this;
      if (!plots) return [];
      return plots.map((plot) => {
        if (isSubPlot(plot)) {
          const { plotId, name } = plot;
          return {
            id: plotId,
            name,
          };
        }
        return {
          id: "default",
          name: this.$t("contestEntryDataForm.defaultPlot"),
        };
      });
    },
    simpleOptions() {
      return (options) =>
        options.map((option) => ({
          id: option,
          name: option,
        }));
    },
    novelImageKey() {
      return (novel) => {
        const { novelId, image } = novel;
        if (image && image.startsWith("file:")) {
          return `file:novels/${novelId}/cover.jpg`;
        }
        return image;
      };
    },
    inputInitialValue() {
      return (type) => {
        if (type === "title") {
          return this.novelTitle;
        }
        if (type === "synopsis") {
          return this.novelSummary;
        }
        return "";
      };
    },
    selectedCharactersCount() {
      return (entryItemId) => {
        const characters = this.internalValue.entryItems[entryItemId];
        return characters ? characters.length : 0;
      };
    },
    selectedManuscriptsCount() {
      return (entryItemId) => {
        const manuscripts = this.internalValue.entryItems[entryItemId];
        return manuscripts ? manuscripts.length : 0;
      };
    },
    selectedManuscriptsTextCount() {
      return (entryItemId) => {
        const manuscripts = this.internalValue.entryItems[entryItemId];
        const textCount = manuscripts
          ? (manuscripts as Manuscript[]).reduce(
              (sum, manuscript) => sum + (manuscript.content ? manuscript.content.length : 0),
              0
            )
          : 0;
        return textCount.toLocaleString();
      };
    },
    errorMessage() {
      return (type) => {
        const error = this.errors.find((error) => error.path === type);
        return error && error.message;
      };
    },

    canEnterContest() {
      const { contest } = this;
      const { applicationStart, applicationEnd } = contest;
      const start = new Date(applicationStart);
      const end = new Date(applicationEnd);
      const now = new Date();

      return compareAsc(now, start) >= 0 && compareAsc(now, end) <= 0;
    },
    contestStatus() {
      const { contest } = this;
      const { applicationStart, applicationEnd } = contest;
      const start = new Date(applicationStart);
      const end = new Date(applicationEnd);
      const now = new Date();

      if (compareAsc(now, start) < 0) return this.$t("contestEntryDataForm.status.notStarted");
      if (compareAsc(now, end) > 0) return this.$t("contestEntryDataForm.status.ended");
      return "";
    },
    nolaNovelAssociated() {
      const id = this.internalValue?.novel?.associatedData?.nolaNovel?.id;
      if (!id) {
        return null;
      }
      const novels = this.$store.getters["nolaNovelModule/novels"];
      return novels?.find((n) => n.id === id);
    },
  },
  methods: {
    async selectCharacterDialog(entryItemId) {
      const dialog = new Dialog(ContestEntryDataDialog);
      const data: ContestEntryDataDialogProps = {
        type: "characters",
        title: this.$t("contestEntryDataForm.dialog.selectCharactersTitle"),
        description: this.$t("contestEntryDataForm.dialog.selectDataDescription"),
        items: this.characters,
        itemsKeyAttribute: "characterId",
        novelId: this.internalValue.novel.id,
        initialSelected: this.internalValue.entryItems[entryItemId],
      };
      const selectedCharacters = await dialog.show(data);
      // NOTE: ここでの代入は参照渡しになるため、Vueのリアクティブに反映される
      this.internalValue.entryItems = { ...this.internalValue.entryItems, [entryItemId]: [...selectedCharacters] };
    },
    async selectManuscriptsDialog(entryItemId) {
      const dialog = new Dialog(ContestEntryDataDialog);
      const data: ContestEntryDataDialogProps = {
        type: "manuscripts",
        title: this.$t("contestEntryDataForm.dialog.selectManuscriptsTitle"),
        description: this.$t("contestEntryDataForm.dialog.selectDataDescription"),
        items: this.manuscripts,
        itemsKeyAttribute: "key",
        novelId: this.internalValue.novel.id,
        initialSelected: this.internalValue.entryItems[entryItemId],
      };
      const selectedManuscripts = await dialog.show(data);
      // NOTE: ここでの代入は参照渡しになるため、Vueのリアクティブに反映される
      this.internalValue.entryItems = { ...this.internalValue.entryItems, [entryItemId]: [...selectedManuscripts] };
    },
    openNolaNovelHelpDialog() {
      const completeDialog = new Dialog(SimpleDialog);
      const completeData: SimpleDialogProps = {
        title: this.$t("contestEntryDataForm.dialog.helpTitle"),
        content: this.$t("contestEntryDataForm.dialog.helpContent"),
        width: 430,
      };
      completeDialog.show(completeData);
    },
    openNolaNovelPage(path: string) {
      const url = process.env.VUE_APP_NOLANOVEL_WEB + path;
      window.open(url, "_blank");
    },
  },
});

interface Props {
  value: { [key: string]: any };
  contest: NolaContest;
  contestImage?: string;
  isConfirm: boolean;
  errors: ValidationError[];
}

interface Computed {
  internalValue: { [key: string]: any };
  contestEntryItems: ContestEntryItemModel[];
  novels: Novel[];
  novel: Novel | undefined;
  plots: (Plot | SubPlot)[];
  characters: Character[];
  manuscripts: Manuscript[];
  novelTitle: string;
  novelSummary: string;
  categoryOptions: Option[];
  novelOptions: Option[];
  plotOptions: Option[];
  simpleOptions(options: any[]): Option[];
  novelImageKey(nobel: Novel): string | undefined;
  inputInitialValue(type: string): string;
  selectedCharactersCount(entryItemId: string): number;
  selectedManuscriptsCount(entryItemId: string): number;
  selectedManuscriptsTextCount(entryItemId: string): string;
  errorMessage(type: string): string | undefined;
  canEnterContest: boolean;
  contestStatus: string;
  nolaNovelAssociated: { [key: string]: any } | null;
}

interface Methods {
  selectCharacterDialog(entryItemId: string): Promise<void>;
  selectManuscriptsDialog(entryItemId: string): Promise<void>;
  openNolaNovelPage(path: string): void;
  openNolaNovelHelpDialog(): void;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.contest-entry-item {
  margin-bottom: 20px;
}

.contest-data {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 20px;
  background-color: var(--color-surface-subtle);

  .contest-image {
    height: 100px;
    margin-right: 20px;
  }

  .contest-name {
    font-weight: var(--font-weight-bold);
    font-size: 1.1em;
    color: var(--color-text-normal);
  }
}

.list-title {
  color: var(--color-text-low);
}

.list {
  max-height: 200px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  box-sizing: border-box;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);
  margin-top: 10px;
  background-color: var(--color-surface-subtle);

  .item {
    box-sizing: border-box;
    padding: 10px 20px;
    color: var(--color-text-normal);
  }
}

.count {
  display: flex;
  justify-content: flex-end;
  margin-top: 5px;
  margin-right: 5px;
  margin-bottom: calc(-1em - 5px);
  color: var(--color-text-low);
}

.contest-status {
  display: flex;
  justify-content: center;
  font-size: 1.2em;
  margin-top: 50px;
  color: var(--color-text-normal);
}

.margin-bottom-10 {
  margin-bottom: 10px;
}
.contest-publish-condition {
  display: flex;
  font-weight: var(--font-weight-bold);
  gap: 3px;
  margin-bottom: 10px;
  color: var(--color-text-normal);

  .publish-condition {
    color: var(--color-primary);
  }
}

.link {
  color: var(--color-primary);
  cursor: pointer;
  text-decoration: underline;
}
.help-icon {
  position: relative;
  @include hover-overlay;
  background-color: var(--color-surface-muted);
  color: var(--color-text-normal);
  border-radius: var(--radius-circle);
  border: none;
  padding: 0;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  display: inline-block;
  cursor: pointer;

  transition: all 0.3s;

}
</style>
<style lang="scss">
@import "@/style-common.scss";

/** ヘルプダイアログのスタイル */
.nola-novel-help-dialog-content {
  margin-inline: 10px;
  font-size: var(--font-size-sm);
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: 10px;

  .head {
    color: var(--color-primary);
    text-align: right;
  }

  .body {
    white-space: pre-wrap;
    text-align: left;
    color: var(--color-text-normal);
  }
}
</style>
